import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { computeMetrics } from "./computeMetrics";
import type {
  Tip,
  LandmarkFrame,
  LandmarkPoint,
  AnalysisEvents,
} from "./computeMetrics";
import { downloadSessionPdf } from "./pdfReport";
import { loadSessions, saveSessions } from "./storage";
import type { SessionRecord } from "./types";
import type { PlayerInfo } from "./AnalyzePage";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Barlow:wght@300;400;500;600&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --bg: #060b18; --bg2: #0a1020; --cyan: #00c8ff; --purple: #8b5cf6;
    --blue: #2563eb; --blue-h: #1d4ed8; --white: #f0f4ff;
    --muted: #4a5568; --border: rgba(255,255,255,0.07);
    --grad: linear-gradient(90deg, #00c8ff, #8b5cf6);
  }

  body { background: var(--bg); color: var(--white); font-family: 'Barlow', sans-serif; }
  .results-wrap { min-height: 100vh; background: var(--bg); overflow-x: hidden; }

  .particles { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
  .particle {
    position: absolute; width: 2px; height: 2px;
    background: var(--cyan); border-radius: 50%; opacity: 0;
    animation: float linear infinite;
  }
  @keyframes float {
    0%   { opacity: 0; transform: translateY(100vh) scale(0); }
    10%  { opacity: 0.4; }
    90%  { opacity: 0.2; }
    100% { opacity: 0; transform: translateY(-10vh) scale(1.5); }
  }

  .topbar {
    position: sticky; top: 0; z-index: 100;
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 28px;
    background: rgba(6,11,24,0.92); backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
  }
  .logo {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 900;
    font-size: 20px; letter-spacing: 2px;
    background: var(--grad); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
  .logo span { font-weight: 300; font-size: 11px; color: var(--muted); -webkit-text-fill-color: var(--muted); display: block; letter-spacing: 3px; }
  .back-btn {
    background: transparent; border: 1px solid var(--border);
    color: var(--white); padding: 8px 18px; border-radius: 6px;
    font-family: 'Barlow', sans-serif; font-size: 13px; cursor: pointer; transition: all 0.2s;
  }
  .back-btn:hover { border-color: var(--cyan); color: var(--cyan); }
  .session-label { font-family: 'Barlow Condensed', sans-serif; font-size: 13px; letter-spacing: 2px; color: var(--muted); text-transform: uppercase; }

  .content { position: relative; z-index: 1; max-width: 1200px; margin: 0 auto; padding: 28px 24px 60px; }
  .section-title {
    font-family: 'Barlow Condensed', sans-serif; font-weight: 800;
    font-size: 13px; letter-spacing: 3px; color: var(--muted);
    text-transform: uppercase; margin-bottom: 14px;
  }

  .video-section { margin-bottom: 28px; }
  .video-container {
    background: #000; border-radius: 12px;
    border: 1px solid var(--border); overflow: hidden;
    position: relative; aspect-ratio: 16/9;
  }
  .video-container video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; }
  .overlay-canvas { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }

  .overlay-controls { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; align-items: center; }
  .overlay-btn {
    padding: 6px 14px; border-radius: 20px;
    font-family: 'Barlow', sans-serif; font-size: 12px; font-weight: 500;
    cursor: pointer; transition: all 0.2s; border: 1px solid;
  }
  .overlay-btn.active-skeleton   { background: rgba(0,200,255,0.15);   border-color: var(--cyan);   color: var(--cyan); }
  .overlay-btn.active-trajectory { background: rgba(139,92,246,0.15); border-color: var(--purple); color: var(--purple); }
  .overlay-btn.active-angles     { background: rgba(251,191,36,0.15);  border-color: #fbbf24;       color: #fbbf24; }
  .overlay-btn.active-ghost      { background: rgba(240,114,182,0.15); border-color: #f472b6;       color: #f472b6; }
  .overlay-btn.active-contact    { background: rgba(34,197,94,0.15);   border-color: #22c55e;       color: #22c55e; }
  .overlay-btn.active-hits       { background: rgba(255,255,255,0.10); border-color: rgba(255,255,255,0.25); color: #f0f4ff; }
  .overlay-btn.inactive { background: transparent; border-color: var(--border); color: var(--muted); }
  .overlay-btn.inactive:hover { border-color: rgba(255,255,255,0.2); color: var(--white); }

  .hits-row {
    margin-top: 10px;
    background: rgba(10,16,32,0.8);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 10px 12px;
  }
  .hits-title {
    font-family: 'Barlow Condensed', sans-serif;
    letter-spacing: 2px;
    color: var(--muted);
    font-size: 11px;
    margin-bottom: 8px;
    text-transform: uppercase;
  }
  .hits-track {
    position: relative;
    height: 10px;
    border-radius: 999px;
    background: rgba(255,255,255,0.06);
    overflow: hidden;
  }
  .hit-tick {
    position: absolute;
    top: -2px;
    width: 2px;
    height: 14px;
    background: rgba(34,197,94,0.95);
    box-shadow: 0 0 10px rgba(34,197,94,0.45);
  }
  .hits-hint {
    margin-top: 8px;
    font-size: 12px;
    color: rgba(240,244,255,0.75);
    line-height: 1.4;
  }

  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 28px; }
  @media(max-width:768px){ .two-col{grid-template-columns:1fr;} }

  .score-card { background: var(--bg2); border: 1px solid var(--border); border-radius: 12px; padding: 20px; }
  .score-rings-grid { display: grid; grid-template-columns: repeat(5,1fr); gap: 12px; }
  .ring-item { display: flex; flex-direction: column; align-items: center; gap: 6px; }
  .ring-svg { width: 64px; height: 64px; }
  .ring-label { font-size: 10px; color: var(--muted); text-align: center; font-family: 'Barlow Condensed', sans-serif; letter-spacing: 1px; text-transform: uppercase; }

  .tips-card { background: var(--bg2); border: 1px solid var(--border); border-radius: 12px; padding: 20px; }
  .tip-item { display: flex; gap: 12px; align-items: flex-start; padding: 12px 0; border-bottom: 1px solid var(--border); }
  .tip-item:last-child { border-bottom: none; padding-bottom: 0; }
  .tip-icon { width: 28px; height: 28px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0; margin-top: 1px; }
  .tip-icon.warn { background: rgba(251,191,36,0.15); }
  .tip-icon.good { background: rgba(34,197,94,0.15); }
  .tip-icon.info { background: rgba(0,200,255,0.15); }
  .tip-title { font-weight: 600; font-size: 13px; color: var(--white); margin-bottom: 3px; }
  .tip-desc { font-size: 12px; color: var(--muted); line-height: 1.5; }

  .panels-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; margin-bottom: 28px; }
  @media(max-width:900px){ .panels-grid{grid-template-columns:repeat(2,1fr);} }
  .panel-card { background: var(--bg2); border: 1px solid var(--border); border-radius: 12px; padding: 18px; }
  .panel-header { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; }
  .panel-icon { width: 30px; height: 30px; border-radius: 7px; display: flex; align-items: center; justify-content: center; font-size: 14px; }
  .panel-title { font-family: 'Barlow Condensed', sans-serif; font-weight: 700; font-size: 14px; letter-spacing: 1px; text-transform: uppercase; }
  .panel-row { display: flex; justify-content: space-between; align-items: center; padding: 5px 0; }
  .panel-row-label { font-size: 11px; color: var(--muted); }
  .panel-row-val { font-family: 'Barlow Condensed', sans-serif; font-weight: 700; font-size: 15px; }
  .cyan{color:var(--cyan);} .purple{color:var(--purple);} .green{color:#22c55e;} .yellow{color:#fbbf24;}

  .metrics-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 10px; }
  .metric-item { background: rgba(255,255,255,0.03); border: 1px solid var(--border); border-radius: 8px; padding: 12px 14px; }
  .metric-label { font-size: 10px; color: var(--muted); letter-spacing: 1px; text-transform: uppercase; margin-bottom: 4px; }
  .metric-value { font-family: 'Barlow Condensed', sans-serif; font-weight: 700; font-size: 22px; color: var(--white); }
  .metric-value span { font-size: 12px; font-weight: 400; color: var(--muted); margin-left: 3px; }
  .metric-highlight { color: var(--cyan); }

  .btn-primary { background: var(--blue); color: var(--white); border: none; padding: 12px 28px; border-radius: 8px; font-family: 'Barlow', sans-serif; font-weight: 600; font-size: 14px; cursor: pointer; transition: background 0.2s; }
  .btn-primary:hover { background: var(--blue-h); }
  .btn-secondary { background: transparent; border: 1px solid var(--border); color: var(--white); padding: 12px 28px; border-radius: 8px; font-family: 'Barlow', sans-serif; font-weight: 500; font-size: 14px; cursor: pointer; transition: all 0.2s; }
  .btn-secondary:hover { border-color: var(--purple); color: var(--purple); }
  .action-row { display: flex; gap: 12px; flex-wrap: wrap; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  .fade-up { animation: fadeUp 0.4s ease both; }
  .delay-1{animation-delay:0.05s} .delay-2{animation-delay:0.1s} .delay-3{animation-delay:0.15s}
`;

const POSE_CONNECTIONS: [number, number][] = [
  [0,1],[1,2],[2,3],[3,7],[0,4],[4,5],[5,6],[6,8],
  [9,10],[11,12],[11,13],[13,15],[15,17],[15,19],[15,21],[17,19],
  [12,14],[14,16],[16,18],[16,20],[16,22],[18,20],
  [11,23],[12,24],[23,24],[23,25],[24,26],[25,27],[26,28],
  [27,29],[28,30],[29,31],[30,32],[27,31],[28,32],
];

function jointColor(idx: number): string {
  if (idx <= 10) return "#00c8ff";
  if (idx >= 11 && idx <= 22) return "#22c55e";
  if (idx >= 23) return "#fbbf24";
  return "#f0f4ff";
}

function boneColor(a: number, b: number): string {
  if (a <= 10 && b <= 10) return "rgba(0,200,255,0.7)";
  if (a >= 23 || b >= 23) return "rgba(251,191,36,0.7)";
  return "rgba(34,197,94,0.7)";
}

function angle3(
  a: { x: number; y: number },
  b: { x: number; y: number },
  c: { x: number; y: number }
): number {
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const mag = Math.sqrt(ab.x**2 + ab.y**2) * Math.sqrt(cb.x**2 + cb.y**2);
  if (mag === 0) return 0;
  return Math.round((Math.acos(Math.min(1, Math.max(-1, dot / mag))) * 180) / Math.PI);
}

type AnalysisResult = {
  videoURL: string;
  frameCount: number;
  duration: number;
  landmarkFrames: LandmarkFrame[];
};

function findFrame(frames: LandmarkFrame[], t: number): LandmarkFrame | null {
  if (!frames.length) return null;
  let closest = frames[0];
  let minDiff = Math.abs(frames[0].timestamp - t);
  for (const f of frames) {
    const d = Math.abs(f.timestamp - t);
    if (d < minDiff) { minDiff = d; closest = f; }
    if (f.timestamp > t + 0.2) break;
  }
  return minDiff < 0.2 ? closest : null;
}

const OVERLAYS = [
  { key: "skeleton",    label: "🦴 Skeleton",      activeClass: "active-skeleton" },
  { key: "trajectory",  label: "🪃 Wrist Trail",   activeClass: "active-trajectory" },
  { key: "angles",      label: "📐 Angles",        activeClass: "active-angles" },
  { key: "ghost",       label: "👻 Ghost Pose",    activeClass: "active-ghost" },
  { key: "contact",     label: "🎯 Contact Zone",  activeClass: "active-contact" },
  { key: "hits",        label: "✨ Hit Markers",   activeClass: "active-hits" },
] as const;

function ScoreRing({ score, label, color }: { score: number; label: string; color: string }) {
  const r = 24; const circ = 2 * Math.PI * r; const dash = (score / 100) * circ;
  return (
    <div className="ring-item">
      <svg className="ring-svg" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
        <circle cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" transform="rotate(-90 32 32)" />
        <text x="32" y="37" textAnchor="middle" fontSize="14" fontWeight="800"
          fontFamily="Barlow Condensed" fill="#f0f4ff">{score}</text>
      </svg>
      <div className="ring-label">{label}</div>
    </div>
  );
}

type OverlayState = {
  skeleton: boolean;
  trajectory: boolean;
  angles: boolean;
  ghost: boolean;
  contact: boolean;
  hits: boolean;
};

export default function ResultsPage(props: {
  onBack: () => void;
  analysis: AnalysisResult;
  player?: { name: string; email: string };
  playerInfo?: PlayerInfo | null;
}) {
  const { onBack, analysis, player, playerInfo } = props;

  const [activeOverlays, setActiveOverlays] = useState<OverlayState>({
    skeleton: true,
    trajectory: true,
    angles: false,
    ghost: true,
    contact: true,
    hits: true,
  });

  // normalize z once
  const normalizedFrames: LandmarkFrame[] = useMemo(() => {
    return analysis.landmarkFrames.map((f) => ({
      ...f,
      landmarks: f.landmarks.map((l) => ({
        ...(l as LandmarkPoint),
        z: (l as any).z ?? 0,
      })),
    }));
  }, [analysis.landmarkFrames]);

  const playerHeightCm = playerInfo?.height ? parseInt(playerInfo.height) : 175;

  const { session, tips: TIPS, events } = useMemo(() => {
    const out = computeMetrics(normalizedFrames, analysis.duration, { playerHeightCm });
    // Backward-compat: if computeMetrics returns no events (older file), fallback.
    const ev: AnalysisEvents =
      (out as any).events ?? ({ hitTimes: [] } as AnalysisEvents);
    return { ...(out as any), events: ev };
  }, [normalizedFrames, analysis.duration, playerHeightCm]);

  // Save session to storage once computed
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (session && !saved) {
      const newRec: SessionRecord = {
        id: Date.now().toString(),
        ...session,
        events,
        label: session.label || (playerInfo?.name ? `${playerInfo.name}'s Session` : "Session"),
      };
      const existing = loadSessions();
      saveSessions([newRec, ...existing]);
      setSaved(true);
    }
  }, [session, saved]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const wristHistoryRef = useRef<{ x: number; y: number }[]>([]);
  const lastHitIdxRef = useRef<number>(-1);

  function toggleOverlay(key: keyof OverlayState) {
    setActiveOverlays((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function pickHittingSide(lm: LandmarkPoint[]) {
    // choose wrist with higher visibility; default right
    const l = lm[15];
    const r = lm[16];
    const lv = l?.visibility ?? 0.5;
    const rv = r?.visibility ?? 0.5;
    return rv >= lv ? "right" : "left";
  }

  function drawGhost(ctx: CanvasRenderingContext2D, frame: LandmarkFrame, W: number, H: number) {
    const lm = frame.landmarks;
    const px = (l: LandmarkPoint) => l.x * W;
    const py = (l: LandmarkPoint) => l.y * H;

    ctx.save();
    ctx.globalAlpha = 0.22;

    POSE_CONNECTIONS.forEach(([a, b]) => {
      if (!lm[a] || !lm[b]) return;
      if ((lm[a].visibility ?? 1) < 0.3 || (lm[b].visibility ?? 1) < 0.3) return;
      ctx.beginPath();
      ctx.moveTo(px(lm[a]), py(lm[a]));
      ctx.lineTo(px(lm[b]), py(lm[b]));
      ctx.strokeStyle = "rgba(240,114,182,0.65)";
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    lm.forEach((l, i) => {
      if ((l.visibility ?? 1) < 0.3) return;
      ctx.beginPath();
      ctx.arc(px(l), py(l), i === 0 ? 5 : 3.5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(240,114,182,0.75)";
      ctx.fill();
    });

    ctx.restore();
  }

  function drawContactZone(ctx: CanvasRenderingContext2D, lm: LandmarkPoint[], W: number, H: number) {
    // "where to hit": suggest contact point slightly above shoulder and a bit forward of wrist line
    const px = (l: LandmarkPoint) => l.x * W;
    const py = (l: LandmarkPoint) => l.y * H;

    const side = pickHittingSide(lm);
    const shoulderIdx = side === "right" ? 12 : 11;
    const elbowIdx = side === "right" ? 14 : 13;
    const wristIdx = side === "right" ? 16 : 15;

    const s = lm[shoulderIdx];
    const e = lm[elbowIdx];
    const w = lm[wristIdx];
    if (!s || !e || !w) return;
    if ((s.visibility ?? 1) < 0.3) return;

    const sx = px(s), sy = py(s);
    const ex = px(e), ey = py(e);
    const wx = px(w), wy = py(w);

    // direction from shoulder -> wrist (arm direction)
    const dx = wx - sx;
    const dy = wy - sy;

    // target point: slightly above shoulder and forward along arm direction
    const tx = sx + dx * 0.35;
    const ty = sy + dy * 0.35 - 26;

    // draw glow ring
    ctx.save();
    ctx.globalAlpha = 0.9;
    ctx.beginPath();
    ctx.arc(tx, ty, 18, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(34,197,94,0.95)";
    ctx.lineWidth = 3;
    ctx.shadowColor = "rgba(34,197,94,0.55)";
    ctx.shadowBlur = 14;
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.fillStyle = "rgba(34,197,94,0.12)";
    ctx.beginPath();
    ctx.arc(tx, ty, 18, 0, Math.PI * 2);
    ctx.fill();

    // label
    ctx.font = "bold 12px Barlow Condensed, sans-serif";
    const label = "CONTACT ZONE";
    const lw = ctx.measureText(label).width;
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(tx - lw / 2 - 8, ty - 34, lw + 16, 18);
    ctx.fillStyle = "#22c55e";
    ctx.fillText(label, tx - lw / 2, ty - 20);

    // small arrow from elbow to target
    ctx.strokeStyle = "rgba(34,197,94,0.7)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(ex, ey);
    ctx.lineTo(tx, ty);
    ctx.setLineDash([4, 3]);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.restore();
  }

  function drawHitMarker(ctx: CanvasRenderingContext2D, W: number, H: number, t: number) {
    // show a small badge for the latest hit moment
    ctx.save();
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = "rgba(34,197,94,0.12)";
    ctx.strokeStyle = "rgba(34,197,94,0.45)";
    ctx.lineWidth = 1;

    const boxW = 170;
    const boxH = 32;
    const x = 12;
    const y = 12;

    ctx.beginPath();
    ctx.roundRect?.(x, y, boxW, boxH, 10);
    if (!ctx.roundRect) {
      // fallback if roundRect missing
      ctx.rect(x, y, boxW, boxH);
    }
    ctx.fill();
    ctx.stroke();

    ctx.font = "bold 12px Barlow Condensed, sans-serif";
    ctx.fillStyle = "#22c55e";
    ctx.fillText(`✨ HIT MOMENT ~ ${t.toFixed(2)}s`, x + 12, y + 20);

    ctx.restore();
  }

  const draw = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) { rafRef.current = requestAnimationFrame(draw); return; }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = video.getBoundingClientRect();
    if (canvas.width !== rect.width || canvas.height !== rect.height) {
      canvas.width = rect.width; canvas.height = rect.height;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const W = canvas.width, H = canvas.height;

    // Current frame
    const frame = findFrame(normalizedFrames, video.currentTime);

    // Previous frame for ghost
    const ghostFrame = activeOverlays.ghost
      ? findFrame(normalizedFrames, Math.max(0, video.currentTime - 0.18))
      : null;

    if (ghostFrame && ghostFrame.landmarks.length > 0) {
      drawGhost(ctx, ghostFrame, W, H);
    }

    if (frame && frame.landmarks.length > 0) {
      const lm = frame.landmarks;
      const px = (l: LandmarkPoint) => l.x * W;
      const py = (l: LandmarkPoint) => l.y * H;

      if (activeOverlays.skeleton) {
        POSE_CONNECTIONS.forEach(([a, b]) => {
          if (!lm[a] || !lm[b]) return;
          if ((lm[a].visibility ?? 1) < 0.3 || (lm[b].visibility ?? 1) < 0.3) return;
          ctx.beginPath();
          ctx.moveTo(px(lm[a]), py(lm[a]));
          ctx.lineTo(px(lm[b]), py(lm[b]));
          ctx.strokeStyle = boneColor(a, b);
          ctx.lineWidth = 2.5;
          ctx.stroke();
        });
        lm.forEach((l, i) => {
          if ((l.visibility ?? 1) < 0.3) return;
          ctx.beginPath();
          ctx.arc(px(l), py(l), i === 0 ? 6 : 4, 0, Math.PI * 2);
          ctx.fillStyle = jointColor(i);
          ctx.fill();
        });
      }

      if (activeOverlays.trajectory && lm[16]) {
        const wx = px(lm[16]), wy = py(lm[16]);
        wristHistoryRef.current.push({ x: wx, y: wy });
        if (wristHistoryRef.current.length > 30) wristHistoryRef.current.shift();
        const trail = wristHistoryRef.current;
        if (trail.length > 2) {
          ctx.beginPath();
          ctx.moveTo(trail[0].x, trail[0].y);
          trail.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
          ctx.strokeStyle = "rgba(139,92,246,0.7)";
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 3]);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        ctx.beginPath();
        ctx.arc(wx, wy, 5, 0, Math.PI * 2);
        ctx.fillStyle = "#8b5cf6";
        ctx.fill();
      }

      if (activeOverlays.angles) {
        const angleJoints: [number, number, number][] = [
          [13, 15, 11], [14, 16, 12], [23, 25, 27], [24, 26, 28],
        ];
        ctx.font = "bold 11px Barlow Condensed, sans-serif";
        angleJoints.forEach(([a, b, c]) => {
          if (!lm[a] || !lm[b] || !lm[c]) return;
          if ((lm[b].visibility ?? 1) < 0.4) return;
          const deg = angle3(
            { x: px(lm[a]), y: py(lm[a]) },
            { x: px(lm[b]), y: py(lm[b]) },
            { x: px(lm[c]), y: py(lm[c]) },
          );
          const tx = px(lm[b]) + 8, ty = py(lm[b]) - 4;
          ctx.fillStyle = "rgba(0,0,0,0.55)";
          ctx.fillRect(tx - 2, ty - 12, 36, 16);
          ctx.fillStyle = "#fbbf24";
          ctx.fillText(`${deg}°`, tx, ty);
        });
      }

      if (activeOverlays.contact) {
        drawContactZone(ctx, lm, W, H);
      }

      if (activeOverlays.hits && events?.hitTimes?.length) {
        // show badge for the next hit near current time (within 0.18s)
        const t = video.currentTime;
        const hits = events.hitTimes;
        // advance index as time goes forward
        let idx = lastHitIdxRef.current;
        while (idx + 1 < hits.length && hits[idx + 1] < t - 0.05) idx++;
        lastHitIdxRef.current = idx;

        const nextIdx = Math.min(idx + 1, hits.length - 1);
        const nextHit = hits[nextIdx];
        if (Math.abs(nextHit - t) < 0.18) {
          drawHitMarker(ctx, W, H, nextHit);
        }
      }
    } else {
      wristHistoryRef.current = [];
    }

    rafRef.current = requestAnimationFrame(draw);
  }, [normalizedFrames, activeOverlays, events]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [draw]);

  function handleSeeked() {
    wristHistoryRef.current = [];
    lastHitIdxRef.current = -1;
  }

  function handleDownloadPdf() {
    downloadSessionPdf({
      playerName: playerInfo?.name || player?.name || "Player",
      playerEmail: player?.email || "",
      handedness: playerInfo?.handedness,
      level: playerInfo?.level,
      session,
      tips: TIPS as Tip[],
    });
  }

  return (
    <>
      <style>{css}</style>
      <div className="results-wrap">
        <div className="particles">
          {Array.from({ length: 16 }).map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`,
              animationDuration: `${8 + Math.random() * 12}s`,
              animationDelay: `${Math.random() * 8}s`,
            }} />
          ))}
        </div>

        <div className="topbar">
          <div className="logo">SHUTTLECOCK <span>BY STATVO</span></div>
          <div className="session-label">{session.label} · {session.date}</div>
          <button className="back-btn" onClick={onBack}>← Dashboard</button>
        </div>

        <div className="content">
          <div className="video-section fade-up">
            <div className="section-title">Annotated Video — {analysis.frameCount} frames analysed</div>

            <div className="video-container">
              <video ref={videoRef} src={analysis.videoURL} controls onSeeked={handleSeeked} />
              <canvas ref={canvasRef} className="overlay-canvas" />
            </div>

            <div className="overlay-controls">
              {OVERLAYS.map((o) => (
                <button
                  key={o.key}
                  className={`overlay-btn ${
                    activeOverlays[o.key] ? o.activeClass : "inactive"
                  }`}
                  onClick={() => toggleOverlay(o.key)}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {activeOverlays.hits && events?.hitTimes?.length ? (
              <div className="hits-row">
                <div className="hits-title">Hit moments (pose heuristic)</div>
                <div className="hits-track">
                  {events.hitTimes.map((t: number, i: number) => {
                    const leftPct = Math.max(
                      0,
                      Math.min(100, (t / Math.max(analysis.duration, 0.01)) * 100)
                    );
                    return <div key={i} className="hit-tick" style={{ left: `${leftPct}%` }} />;
                  })}
                </div>
                <div className="hits-hint">
                  Green ticks mark likely contact moments (wrist acceleration peak). Use Contact Zone overlay to see suggested strike point.
                </div>
              </div>
            ) : null}
          </div>

          <div className="two-col fade-up delay-1">
            <div className="score-card">
              <div className="section-title">Form Scores</div>
              <div className="score-rings-grid">
                <ScoreRing score={session.overall}  label="Overall"  color="#00c8ff" />
                <ScoreRing score={session.smash}    label="Smash"    color="#8b5cf6" />
                <ScoreRing score={session.footwork} label="Footwork" color="#22c55e" />
                <ScoreRing score={session.landing}  label="Landing"  color="#fbbf24" />
                <ScoreRing score={session.prep}     label="Prep"     color="#f472b6" />
              </div>
            </div>

            <div className="tips-card">
              <div className="section-title">AI Suggestions</div>
              {(TIPS as Tip[]).map((t, i: number) => (
                <div className="tip-item" key={i}>
                  <div className={`tip-icon ${t.type}`}>{t.icon}</div>
                  <div>
                    <div className="tip-title">{t.title}</div>
                    <div className="tip-desc">{t.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="section-title fade-up delay-2">Full Metrics</div>
          <div className="panels-grid fade-up delay-2">
            <div className="panel-card">
              <div className="panel-header">
                <div className="panel-icon" style={{ background:"rgba(0,200,255,0.12)" }}>🦘</div>
                <div className="panel-title cyan">Jump</div>
              </div>
              <div className="panel-row"><span className="panel-row-label">Height</span><span className="panel-row-val cyan">{session.jumpHeight} cm</span></div>
              <div className="panel-row"><span className="panel-row-label">Hang Time</span><span className="panel-row-val cyan">{session.hangTime}s</span></div>
              <div className="panel-row"><span className="panel-row-label">Total Jumps</span><span className="panel-row-val">{session.jumpCount}</span></div>
              <div className="panel-row"><span className="panel-row-label">Avg Height</span><span className="panel-row-val">{Math.round(session.jumpHeight * 0.85)} cm</span></div>
            </div>

            <div className="panel-card">
              <div className="panel-header">
                <div className="panel-icon" style={{ background:"rgba(139,92,246,0.12)" }}>🏃</div>
                <div className="panel-title purple">Court Coverage</div>
              </div>
              <div className="panel-row"><span className="panel-row-label">Displacement</span><span className="panel-row-val purple">{(session.displacement / 100).toFixed(1)} m</span></div>
              <div className="panel-row"><span className="panel-row-label">Dir. Changes</span><span className="panel-row-val purple">{session.dirChanges}</span></div>
              <div className="panel-row"><span className="panel-row-label">Lunge Count</span><span className="panel-row-val">{session.lungeCount}</span></div>
              <div className="panel-row"><span className="panel-row-label">Recovery Time</span><span className="panel-row-val">{session.recoveryTime}s</span></div>
            </div>

            <div className="panel-card">
              <div className="panel-header">
                <div className="panel-icon" style={{ background:"rgba(251,191,36,0.12)" }}>📐</div>
                <div className="panel-title yellow">Angles</div>
              </div>
              <div className="panel-row"><span className="panel-row-label">Back Arch</span><span className="panel-row-val yellow">{session.backArch}°</span></div>
              <div className="panel-row"><span className="panel-row-label">Knee Bend</span><span className="panel-row-val yellow">{session.kneeBend}°</span></div>
              <div className="panel-row"><span className="panel-row-label">Stance Width</span><span className="panel-row-val yellow">{session.stanceWidth} cm</span></div>
              <div className="panel-row"><span className="panel-row-label">Wrist Speed (beta)</span><span className="panel-row-val yellow">{session.wristSpeed}</span></div>
            </div>
          </div>

          <div className="two-col fade-up delay-3">
            <div className="score-card">
              <div className="section-title">Movement</div>
              <div className="metrics-grid">
                <div className="metric-item"><div className="metric-label">Lunge Count</div><div className="metric-value metric-highlight">{session.lungeCount}</div></div>
                <div className="metric-item"><div className="metric-label">Lunge Depth</div><div className="metric-value">{session.lungeDepth}<span>cm</span></div></div>
                <div className="metric-item"><div className="metric-label">Recovery</div><div className="metric-value">{session.recoveryTime}<span>s</span></div></div>
                <div className="metric-item"><div className="metric-label">Duration</div><div className="metric-value">{session.duration}</div></div>
              </div>
            </div>

            <div className="score-card" style={{ display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
              <div>
                <div className="section-title">Export</div>
                <div style={{ fontSize:"12px", color:"var(--muted)", lineHeight:1.6, marginBottom:"16px" }}>
                  Download a PDF report with metrics and suggestions.
                </div>
              </div>
              <div className="action-row">
                <button className="btn-primary" onClick={handleDownloadPdf}>⬇ Download PDF</button>
                
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}